import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import { useServiceTypes } from '@/hooks/catalog-admin';
import { useClient, useClients } from '@/hooks/clients';
import { usePropertyCategories } from '@/hooks/property-categories';
import { useCreateRecurringRule, useUpdateRecurringRule } from '@/hooks/recurring-jobs';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import { todayInBusinessZone } from '@shared/dates';
import { WEEKDAY_LABELS } from '@shared/schemas/client';
import { MAX_PAGE_SIZE } from '@shared/schemas/pagination';
import {
  GENERATION_HORIZON_DAYS,
  RECURRING_FREQUENCIES,
  RECURRING_FREQUENCY_LABELS,
  occurrencesBetween,
  type RecurringFrequencyValue,
  type RecurringRuleView,
} from '@shared/schemas/recurring';

/*
 * Create or edit a standing arrangement — ROADMAP.md §7.5.
 *
 * **The preview at the bottom is the point of the screen.** "Every two weeks on
 * Tuesdays" is a sentence somebody can misread; four dates are not. It runs
 * `occurrencesBetween` — the *same* pure function the nightly generator uses —
 * so what the office is shown here is exactly what will appear on the calendar.
 * A preview computed a second way would be a second answer, and the one nobody
 * checks is the one that is wrong.
 *
 * The client and the property are fixed once the rule exists. A standing
 * arrangement that changes address is a different arrangement, and the jobs
 * already generated carry the old one — `address_snapshot` is written once per
 * job on purpose (business-rules.md §3).
 */

export function RecurringRuleDialog({
  open,
  onOpenChange,
  rule,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule?: RecurringRuleView;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <RuleForm
            rule={rule}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function RuleForm({ rule, onDone }: { rule?: RecurringRuleView; onDone: () => void }) {
  const toast = useToast();
  const isEdit = rule !== undefined;
  const today = todayInBusinessZone();

  const [clientSearch, setClientSearch] = useState('');
  const [clientId, setClientId] = useState(rule?.clientId ?? '');
  const [propertyId, setPropertyId] = useState(rule?.propertyId ?? '');
  const [categoryId, setCategoryId] = useState(rule?.categoryId ?? '');
  const [serviceTypeId, setServiceTypeId] = useState(rule?.serviceTypeId ?? '');
  const [frequency, setFrequency] = useState<RecurringFrequencyValue>(rule?.frequency ?? 'weekly');
  const [dayOfWeek, setDayOfWeek] = useState(String(rule?.dayOfWeek ?? 2));
  const [dayOfMonth, setDayOfMonth] = useState(String(rule?.dayOfMonth ?? 1));
  const [preferredTime, setPreferredTime] = useState(rule?.preferredTime.slice(0, 5) ?? '09:00');
  const [duration, setDuration] = useState(String(rule?.estimatedDurationMinutes ?? 120));
  const [startDate, setStartDate] = useState(rule?.startDate ?? today);
  const [endDate, setEndDate] = useState(rule?.endDate ?? '');

  const clients = useClients({
    sort: 'name',
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    search: clientSearch,
    activity: 'active',
    highPriorityOnly: false,
  });
  const clientDetail = useClient(clientId === '' ? undefined : clientId);
  const categories = usePropertyCategories({
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    search: '',
    activity: 'active',
  });
  const services = useServiceTypes({
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    search: '',
    activity: 'active',
  });

  const create = useCreateRecurringRule();
  const update = useUpdateRecurringRule(rule?.id ?? '');
  const mutation = isEdit ? update : create;
  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  const properties = (clientDetail.data?.properties ?? []).filter((entry) => entry.isActive);

  /*
   * **The same function the nightly generator runs.** Not an approximation of
   * it — the preview and the calendar have to agree, and the only way to
   * guarantee that is for them to be one implementation.
   */
  const preview = occurrencesBetween(
    {
      frequency,
      dayOfWeek: frequency === 'monthly' ? null : Number(dayOfWeek),
      dayOfMonth: frequency === 'monthly' ? Number(dayOfMonth) : null,
      startDate: startDate === '' ? today : startDate,
      endDate: endDate === '' ? null : endDate,
    },
    startDate === '' ? today : startDate,
    // Three months, so a monthly rule shows more than one date.
    addDays(startDate === '' ? today : startDate, 92),
  ).slice(0, 5);

  function submit(event: FormEvent) {
    event.preventDefault();

    const shared = {
      categoryId: categoryId === '' ? null : categoryId,
      serviceTypeId,
      frequency,
      dayOfWeek: frequency === 'monthly' ? null : Number(dayOfWeek),
      dayOfMonth: frequency === 'monthly' ? Number(dayOfMonth) : null,
      preferredTime,
      estimatedDurationMinutes: Number(duration) || 0,
      startDate,
      endDate: endDate === '' ? null : endDate,
    };

    const handlers = {
      onSuccess: (saved: RecurringRuleView) => {
        toast.success(
          isEdit
            ? `Saved ${saved.clientName}'s ${saved.frequencyLabel.toLowerCase()} clean.`
            : // Says when it starts producing, because "created" alone leaves
              // somebody wondering whether anything happened.
              `Added. Jobs appear on the calendar up to ${String(GENERATION_HORIZON_DAYS)} days ahead.`,
        );
        onDone();
      },
    };

    if (isEdit) {
      update.mutate(shared, handlers);
    } else {
      create.mutate(
        { ...shared, clientId, propertyId: propertyId === '' ? null : propertyId },
        handlers,
      );
    }
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {isEdit ? `Edit ${rule.clientName}'s recurring job` : 'Add a recurring job'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        Jobs appear on the calendar {GENERATION_HORIZON_DAYS} days ahead, by themselves.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        {isEdit ? (
          // Fixed once the rule exists. A standing arrangement that changes
          // address is a different arrangement, and the jobs already generated
          // carry the old one.
          <div className="bg-paper-50 rounded-sm border border-ink-300 px-3 py-2.5">
            <p className="text-label text-ink-900">{rule.clientName}</p>
            <p className="text-meta text-ink-500">
              {rule.propertyAddress ?? 'Their primary property'} — to move this to another address,
              add a new recurring job and pause this one.
            </p>
          </div>
        ) : (
          <>
            <Field
              label="Search by name or phone"
              name="clientSearch"
              type="search"
              value={clientSearch}
              onChange={(event) => {
                setClientSearch(event.target.value);
              }}
              placeholder="Name, business, phone or email"
            />

            <SelectField
              label="Client"
              name="clientId"
              required
              value={clientId}
              onChange={(id) => {
                setClientId(id);
                // A different client means a different set of properties — a
                // stale pick would book a clean at the wrong house.
                setPropertyId('');
              }}
              error={fieldErrors.clientId}
            >
              <option value="">Choose a client</option>
              {(clients.data?.data ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.displayName}
                </option>
              ))}
            </SelectField>

            <SelectField
              label="Property"
              name="propertyId"
              value={propertyId}
              onChange={setPropertyId}
              disabled={clientId === ''}
              hint="Leave it on the primary property and the generator uses whichever that is at the time."
              error={fieldErrors.propertyId}
            >
              <option value="">
                {clientId === '' ? 'Choose a client first' : 'Their primary property'}
              </option>
              {properties.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.nickname ? `${entry.nickname} — ${entry.fullAddress}` : entry.fullAddress}
                </option>
              ))}
            </SelectField>
          </>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Checklist"
            name="categoryId"
            value={categoryId}
            onChange={setCategoryId}
            hint="Copied onto each job as it is created."
          >
            <option value="">No checklist</option>
            {(categories.data?.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Service"
            name="serviceTypeId"
            required
            value={serviceTypeId}
            onChange={(id) => {
              setServiceTypeId(id);
              const service = (services.data?.data ?? []).find((entry) => entry.id === id);
              if (service) setDuration(String(service.defaultDurationMinutes));
            }}
            error={fieldErrors.serviceTypeId}
          >
            <option value="">Choose a service</option>
            {(services.data?.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </SelectField>
        </div>

        <fieldset>
          <legend className="text-label text-ink-900">How often</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {RECURRING_FREQUENCIES.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={frequency === value}
                onClick={() => {
                  setFrequency(value);
                }}
                className={cn(
                  'inline-flex min-h-touch min-w-touch items-center justify-center rounded-full px-3.5 text-label',
                  frequency === value
                    ? 'bg-honey-400 text-ink-900'
                    : 'border border-ink-300 bg-white text-ink-700 hover:bg-honey-50',
                )}
              >
                {/* The display value. `biweekly` never reaches a screen (P5). */}
                {RECURRING_FREQUENCY_LABELS[value]}
              </button>
            ))}
          </div>
        </fieldset>

        {frequency === 'monthly' ? (
          <Field
            label="Day of the month"
            name="dayOfMonth"
            type="number"
            min={1}
            max={31}
            required
            value={dayOfMonth}
            error={fieldErrors.dayOfMonth}
            onChange={(event) => {
              setDayOfMonth(event.target.value);
            }}
            // The clamp is the generator's, and it is worth saying: skipping
            // February would mean a customer quietly not cleaned that month.
            hint="A date past the end of a short month lands on its last day rather than being skipped."
          />
        ) : (
          <SelectField
            label="Day of the week"
            name="dayOfWeek"
            required
            value={dayOfWeek}
            onChange={setDayOfWeek}
            error={fieldErrors.dayOfWeek}
          >
            {WEEKDAY_LABELS.map((label, index) => (
              <option key={label} value={String(index)}>
                {label}
              </option>
            ))}
          </SelectField>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Time"
            name="preferredTime"
            type="time"
            required
            value={preferredTime}
            error={fieldErrors.preferredTime}
            onChange={(event) => {
              setPreferredTime(event.target.value);
            }}
          />
          <Field
            label="Length in minutes"
            name="estimatedDurationMinutes"
            type="number"
            min={15}
            max={1440}
            required
            value={duration}
            error={fieldErrors.estimatedDurationMinutes}
            onChange={(event) => {
              setDuration(event.target.value);
            }}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Starts"
            name="startDate"
            type="date"
            required
            value={startDate}
            error={fieldErrors.startDate}
            onChange={(event) => {
              setStartDate(event.target.value);
            }}
          />
          <Field
            label="Ends"
            name="endDate"
            type="date"
            value={endDate}
            error={fieldErrors.endDate}
            onChange={(event) => {
              setEndDate(event.target.value);
            }}
            hint="Leave it empty to keep going."
          />
        </div>

        {/*
          **The preview, from the generator's own arithmetic.** A sentence can
          be misread; four dates cannot. It is what catches "every other
          Tuesday" meaning a different fortnight than somebody expected, before
          it is a customer's complaint.
        */}
        <div className="bg-paper-50 rounded-sm border border-ink-300 p-3">
          <p className="text-label text-ink-900">The next few</p>
          {preview.length === 0 ? (
            <p className="mt-1 text-meta text-ink-500">
              Nothing in the next three months — check the day and the dates above.
            </p>
          ) : (
            <ul className="mt-1.5 flex flex-wrap gap-2">
              {preview.map((date) => (
                <li
                  key={date}
                  className="rounded-full bg-white px-2.5 py-1 text-meta text-ink-900 ring-1 ring-ink-300"
                >
                  {new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    timeZone: 'UTC',
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button type="button" variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add recurring job'}
          </Button>
        </div>
      </form>
    </>
  );
}

function addDays(day: string, delta: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + delta * 86_400_000).toISOString().slice(0, 10);
}

/** A labelled `<select>`. The same shape `Field` gives an `<input>`. */
function SelectField({
  label,
  name,
  value,
  onChange,
  children,
  required,
  disabled,
  hint,
  error,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  required?: boolean;
  disabled?: boolean;
  hint?: string;
  error?: string;
}) {
  const id = `recurring-${name}`;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <select
        id={id}
        name={name}
        value={value}
        required={required}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={cn(
          'min-h-control w-full rounded-sm border bg-white px-3 text-body',
          'disabled:bg-paper-50 disabled:text-ink-500',
          error ? 'border-status-priority-text' : 'border-ink-300',
        )}
      >
        {children}
      </select>
      {hint ? <p className="text-meta text-ink-500">{hint}</p> : null}
      {error ? (
        <p role="alert" className="text-meta text-status-priority-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}
